/**
 * Builder Mode homebrew overlay.
 *
 * Builder Mode rebuilds an existing sheet from static SRD data and so never saw
 * session homebrew — a character on a homebrew class/race couldn't be rebuilt
 * (the class/race wasn't listed and didn't resolve). These helpers merge the
 * session's homebrew packages into the base SRD lists, reusing the SAME shared
 * class mapper as the server overlay / level-up path so a homebrew class carries
 * merged proficiencies and derived spell slots consistently.
 *
 * Raw `entry.data` access is routed through the SINGLE homebrew data schema
 * (`parseHomebrewData`, @roll1/shared/srd/homebrew-data-schema) instead of the
 * former mirrored `(e.data.X as T) ?? default` casts. The schema applies the
 * SAME per-field defaults verbatim (race speed 30, spell school 'Evocation',
 * classes []), so behavior for valid homebrew is unchanged; divergent-shape
 * fields (race abilityBonuses, item weaponData/armorData) stay projected in the
 * consumer via the schema's passthrough record.
 */

import { useMemo } from 'react';
import { srdClasses } from '@roll1/shared/srd/classes';
import { srdRaces } from '@roll1/shared/srd/races';
import { homebrewClassDataToSrdClass } from '@roll1/shared/srd/homebrew-class-mapper';
import {
  foldHomebrewSubclassFeatures,
  slugify,
  type HbSubclassEntry,
} from '@roll1/shared/systems/dnd5e/srd/homebrew-subclass.js';
import { isValidSpellAoe } from '@roll1/shared/spell-aoe';
import { srdSpellMap, srdEquipmentMap } from '@roll1/shared/systems/dnd5e/srd/index.js';
import { parseHomebrewData } from '@roll1/shared/srd/homebrew-data-schema.js';
import type {
  HomebrewRaceData,
  HomebrewSpellData,
  HomebrewClassData,
} from '@roll1/shared/srd/homebrew-data-schema.js';
import type { SrdClass, SrdRace } from '@roll1/shared/srd/types';
import type { SrdSpell, SrdEquipmentItem } from '@roll1/shared/systems/dnd5e/srd/types.js';
import type { HomebrewSessionEntry, HomebrewSessionPackage } from '@roll1/shared';
import { useOptionalSessionContext } from '../../../contexts/SessionContext';

/**
 * Validate `entry.data` against the single homebrew data schema and return the
 * validated record. On a hard schema failure (only 'feat', which has no data
 * schema) the raw `entry.data` is returned unchanged so callers project the same
 * value they would have read from a bare cast. The passthrough schemas keep an
 * index signature, so the result is a `Record<string, unknown>` regardless of
 * which per-type branch matched — divergent-shape fields stay accessible.
 */
function validatedData(
  entry: HomebrewSessionEntry,
  packageId: string,
): Record<string, unknown> {
  const parsed = parseHomebrewData(entry.type, entry.data, {
    packageId,
    slug: entry.slug,
  });
  return parsed.ok ? (parsed.value as Record<string, unknown>) : entry.data;
}

/**
 * Base SRD classes + connected homebrew classes (mapped to the engine shape),
 * with homebrew subclass features folded into their parent class.
 *
 * The SRD class constants share the global feature arrays, so each SRD class is
 * shallow-cloned before folding: `foldHomebrewSubclassFeatures` reassigns a NEW
 * `features` array on the clone, leaving the shared SRD constants' properties
 * untouched. Homebrew "subclass" entries are collected into `HbSubclassEntry[]`
 * and folded in one pass, matching the server overlay so the client engine sees
 * the same merged classes.
 *
 * Class `entry.data` is validated/defaulted through `HomebrewClassDataSchema`
 * (via `parseHomebrewData`) BEFORE being handed to `homebrewClassDataToSrdClass`
 * — the mapper reads extractor-shape fields that survive as passthrough keys, so
 * its input contract (`Record<string, unknown>`) is unchanged.
 */
export function mergedBuilderClasses(
  homebrewPackages: HomebrewSessionPackage[] | undefined,
): SrdClass[] {
  const out: SrdClass[] = srdClasses.map((c: SrdClass): SrdClass => ({ ...c }));
  const subclassEntries: HbSubclassEntry[] = [];
  for (const pkg of homebrewPackages ?? []) {
    for (const e of pkg.entries) {
      if (e.type === 'class') {
        const data: HomebrewClassData = validatedData(e, pkg.packageId) as HomebrewClassData;
        out.push(homebrewClassDataToSrdClass(e.slug, e.name, data));
      } else if (e.type === 'subclass') {
        subclassEntries.push({
          slug: e.slug,
          name: e.name,
          data: validatedData(e, pkg.packageId),
        });
      }
    }
  }
  foldHomebrewSubclassFeatures(out, subclassEntries);
  return out;
}

/** A selectable subclass in Builder Mode, tagged with its parent class slug. */
export interface BuilderSubclassOption {
  slug: string;
  name: string;
  parentClass: string;
}

/**
 * Base SRD subclasses + connected homebrew subclasses. Each SRD class exposes at
 * most one subclass (`srdClass.subclass`), keyed by `slugify(name)`; homebrew
 * "subclass" entries carry their parent class slug in `data.parentClass`. When
 * `parentClassSlug` is given, the result is narrowed to that class. Mirrors
 * `mergedBuilderRaces` in style.
 */
export function mergedBuilderSubclasses(
  homebrewPackages: HomebrewSessionPackage[] | undefined,
  parentClassSlug?: string,
): BuilderSubclassOption[] {
  const out: BuilderSubclassOption[] = [];
  for (const cls of srdClasses) {
    if (cls.subclass) {
      out.push({
        slug: slugify(cls.subclass.name),
        name: cls.subclass.name,
        parentClass: cls.slug,
      });
    }
  }
  for (const pkg of homebrewPackages ?? []) {
    for (const e of pkg.entries) {
      if (e.type === 'subclass') {
        const data = validatedData(e, pkg.packageId);
        out.push({
          slug: e.slug,
          name: e.name,
          parentClass: (data.parentClass as string) ?? '',
        });
      }
    }
  }
  return parentClassSlug
    ? out.filter((s: BuilderSubclassOption): boolean => s.parentClass === parentClassSlug)
    : out;
}

/** Base SRD races + connected homebrew races (mirrors server session-srd getRaces). */
export function mergedBuilderRaces(
  homebrewPackages: HomebrewSessionPackage[] | undefined,
): SrdRace[] {
  const out: SrdRace[] = [...srdRaces];
  for (const pkg of homebrewPackages ?? []) {
    for (const e of pkg.entries) {
      if (e.type === 'race') {
        // Schema-validated view: `speed` (default 30), `traits`/`languages`/
        // `subraces` (default []) are applied by HomebrewRaceDataSchema verbatim.
        const data = validatedData(e, pkg.packageId) as HomebrewRaceData;
        out.push({
          slug: e.slug,
          name: e.name,
          speed: data.speed,
          // size/abilityBonuses/alignment/age/*Description diverge from the SRD
          // shape (or are passthrough-only), so they stay projected here to keep
          // valid-homebrew output byte-identical to the former cast.
          size: (data.size as string) ?? 'Medium',
          abilityBonuses: (data.abilityBonuses as SrdRace['abilityBonuses']) ?? [],
          alignment: (data.alignment as string) ?? '',
          age: (data.age as string) ?? '',
          sizeDescription: (data.sizeDescription as string) ?? '',
          languages: data.languages,
          languageDescription: (data.languageDescription as string) ?? '',
          traits: data.traits as SrdRace['traits'],
          subraces: data.subraces as SrdRace['subraces'],
        });
      }
    }
  }
  return out;
}

/**
 * Base SRD spells + connected homebrew spells, indexed by slug. Mirrors the
 * server session-srd getSpells mapping so the HUD sheet adapter can enrich
 * homebrew spellbook entries with school/level/components/etc.
 */
export function mergedSpellMap(pkgs: HomebrewSessionPackage[]): Map<string, SrdSpell> {
  const out = new Map<string, SrdSpell>(srdSpellMap);
  for (const pkg of pkgs) {
    for (const e of pkg.entries) {
      if (e.type === 'spell') {
        // Schema-validated view: `school` (default 'Evocation') and `classes`
        // (default []) are applied by HomebrewSpellDataSchema verbatim.
        const data = validatedData(e, pkg.packageId) as HomebrewSpellData;
        out.set(e.slug, {
          slug: e.slug,
          name: e.name,
          level: (data.level as number) ?? 0,
          school: data.school as SrdSpell['school'],
          castingTime: (data.castingTime as string) ?? '1 action',
          range: (data.range as string) ?? 'Self',
          // `components` is a string in both the extractor and the schema
          // (default 'V, S'), matching the SRD `string` field directly.
          components: data.components ?? 'V, S',
          duration: (data.duration as string) ?? 'Instantaneous',
          concentration: (data.concentration as boolean) ?? false,
          ritual: (data.ritual as boolean) ?? false,
          classes: data.classes,
          description: (data.description as string) ?? '',
          // A homebrew spell's area (passthrough field `areaOfEffect: { shape, sizeFt }`) — the aim icon of the HUD uses it.
          ...(isValidSpellAoe(data.areaOfEffect) ? { areaOfEffect: data.areaOfEffect as NonNullable<SrdSpell['areaOfEffect']> } : {}),
        });
      }
    }
  }
  return out;
}

/**
 * Base SRD equipment + connected homebrew items, indexed by slug. Homebrew item
 * data nests armorData / weaponData whereas SrdEquipmentItem holds those fields
 * flat, so they are flattened here (R5: raw projection/flattening stays in the
 * consumer — the schema keeps the nested weaponData/armorData sub-shapes raw).
 */
export function mergedEquipmentMap(pkgs: HomebrewSessionPackage[]): Map<string, SrdEquipmentItem> {
  const out = new Map<string, SrdEquipmentItem>(srdEquipmentMap);
  for (const pkg of pkgs) {
    for (const e of pkg.entries) {
      if (e.type === 'item') {
        const data = validatedData(e, pkg.packageId);
        const armor = (data.armorData as Record<string, unknown> | undefined) ?? {};
        const weapon = (data.weaponData as Record<string, unknown> | undefined) ?? {};
        out.set(e.slug, {
          slug: e.slug,
          name: e.name,
          category: (data.category as string) ?? '',
          cost: (data.cost as string | null) ?? null,
          weight: (data.weight as number | null) ?? null,
          // Weapon (flattened from weaponData)
          weaponCategory: weapon.weaponCategory as string | undefined,
          weaponRange: weapon.weaponRange as string | undefined,
          damageDice: weapon.damageDice as string | undefined,
          damageType: weapon.damageType as string | null | undefined,
          twoHandedDice: weapon.twoHandedDice as string | undefined,
          rangeNormal: weapon.rangeNormal as number | null | undefined,
          rangeLong: weapon.rangeLong as number | null | undefined,
          properties: weapon.properties as string[] | undefined,
          throwRange: weapon.throwRange as number | undefined,
          throwRangeLong: weapon.throwRangeLong as number | undefined,
          // Armor (flattened from armorData)
          armorCategory: armor.armorCategory as string | undefined,
          acBase: armor.acBase as number | undefined,
          acDexBonus: armor.acDexBonus as boolean | undefined,
          acMaxBonus: armor.acMaxBonus as number | null | undefined,
          strMinimum: armor.strMinimum as number | undefined,
          stealthDisadvantage: armor.stealthDisadvantage as boolean | undefined,
          // Gear
          description: data.description as string | undefined,
        });
      }
    }
  }
  return out;
}

/**
 * Homebrew-merged SRD lists for Builder Mode. Reads the session overlay from
 * context (null outside a session — demo/tests → base SRD only).
 */
export function useBuilderSrd(): { classes: SrdClass[]; races: SrdRace[] } {
  const session = useOptionalSessionContext();
  const pkgs = session?.mp.serverState?.homebrewPackages;
  const classes = useMemo(() => mergedBuilderClasses(pkgs), [pkgs]);
  const races = useMemo(() => mergedBuilderRaces(pkgs), [pkgs]);
  return { classes, races };
}
